import { FC, memo, useCallback, useEffect, useMemo } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { updateDraft } from '../store/user';

// hooks
import { useMonthYearFormat } from '../hooks/useMonthYearFormat';

// assets
import style from '../assets/style/components/edit.module.scss';

// type
import { BirthMonth } from '../@types/profile';

// function component
export const EditBirthMonth: FC = () => {
  const dispatch = useDispatch();
  const formatBirthMonth = useMonthYearFormat();

  // 見出し
  const { isUserRegistered, hasEmptyFields } = useSelector((state: RootState) => state.user);

  // 初回登録かどうか
  const isRegistered = useMemo(
    () => isUserRegistered && !hasEmptyFields,
    [isUserRegistered, hasEmptyFields]
  );

  type InformationProps = {
    isEdit: boolean;
  };
  const Information = memo<InformationProps>(({ isEdit }) => (
    <h3 className={style.question}>
      {isEdit || hasEmptyFields
        ? '生まれた年と月を教えてください'
        : '生まれた年と月を変更しますか？'}
    </h3>
  ));

  // 初期値
  const defaultValue = useSelector((state: RootState) => state.user?.draft?.birthMonth);

  // デフォルト値（現在日の40年前の月）
  const getDefaultDateForStore = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear() - 40;
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}-01`;
  }, []);

  const getDefaultDateForInput = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear() - 40;
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }, []);

  // 値を変更するとstoreへ反映
  const doChange = useCallback(
    e => {
      // YYYY-MMをYYYY-MM-01に変換
      const monthValue = e.target.value;
      if (!monthValue) return; // UIからの入力値が空である場合を却下
      const newValue = `${monthValue}-01` as BirthMonth;
      dispatch(updateDraft({ label: 'birthMonth', value: newValue }));
    },
    [dispatch]
  );

  // オンボーディング時、初期値をstoreに反映
  useEffect(() => {
    if (!isRegistered && !defaultValue) {
      dispatch(updateDraft({ label: 'birthMonth', value: getDefaultDateForStore() as BirthMonth }));
    }
  }, [dispatch, isRegistered, defaultValue, getDefaultDateForStore]);

  return (
    <>
      <Information isEdit={!isRegistered} />
      <label className={style.month} htmlFor="birthMonth">
        <span>
          {formatBirthMonth(defaultValue ?? (!isRegistered ? getDefaultDateForStore() : ''))}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
        </svg>
        <input
          type="month"
          id="birthMonth"
          defaultValue={
            defaultValue?.slice(0, 7) ?? (!isRegistered ? getDefaultDateForInput() : '')
          }
          onChange={doChange}
        />
      </label>
    </>
  );
};
